import {
  formatPaymentMethod,
  STRIPE_PAYMENT_METHOD_ID,
} from '@amblique/king-living/app/pages/checkout/util/stripe-payment-method-label';
import {
  applyGa4CartItemCategories,
  resolveAddToCartItemVariant,
  toGa4AddToCartItem,
  toGa4AddToCartItemPartial,
} from '@amblique/king-living/app/analytics/ga4/item-mapper';
import {roundGa4Currency} from '@amblique/king-living/app/analytics/ga4/currency';
import {parseSelectedOptionsUIPayload, collectBasketCatalogProductIds} from '@amblique/king-living/app/utils/cart-utils';
import {
  buildCylindoSnapshotImageUrl,
  mergeCylindoConfiguration,
} from '@amblique/king-living/app/utils/cylindo-params';
import {getSelectedVariantProduct} from '@amblique/king-living/app/utils/customize-selections';
import {resolveGalleryResourceForProduct} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {isSimpleProduct} from '@amblique/king-living/app/utils/product-utils';
import {resolveAbsoluteImageUrl} from '@amblique/king-living/app/utils/responsive-image-src';

const absGa4ImageUrl = (raw) => (raw ? resolveAbsoluteImageUrl(raw) || '' : '');

/** Per line SKU — PIM → Cylindo → SFCC via {@link resolveGalleryResourceForProduct}. */
export const resolveGa4ProductImageUrl = (product, {payloadCylindoUrl = null} = {}) => {
  if (!product) {
    return absGa4ImageUrl(payloadCylindoUrl);
  }

  const video = product.c_video ?? product.video;
  const galleryResource = resolveGalleryResourceForProduct(
    video != null && String(video).trim() !== '' ?
      {...product, c_video: undefined, video: undefined} :
      product,
    {allowCylindo: !isSimpleProduct(product)}
  );
  const pimImage = (galleryResource.pimImages ?? []).find(
    (entry) => entry.mediaType !== 'video' && (entry.disBaseLink || entry.link)
  );
  const pimUrl = pimImage?.disBaseLink || pimImage?.link;

  if (pimUrl) {
    return absGa4ImageUrl(pimUrl);
  }

  if (galleryResource.cylindoId && galleryResource.customerId) {
    const cylindoUrl = buildCylindoSnapshotImageUrl(
      galleryResource.cylindoId,
      galleryResource.customerId,
      mergeCylindoConfiguration([], galleryResource.cylindoFeatures ?? []) || undefined,
      {}
    );

    if (cylindoUrl) {
      return absGa4ImageUrl(cylindoUrl);
    }
  }

  return absGa4ImageUrl(payloadCylindoUrl);
};

export const resolveGa4PrimaryCoverImage = ({
  basketLine,
  coverVariant,
  optionImageMap,
  payload,
  primaryItem,
  productsById = {},
}) => {
  const primaryId = primaryItem?.productId;
  const fromFetch = primaryId ? optionImageMap?.[primaryId] : null;

  if (fromFetch) {
    return fromFetch;
  }

  const product = (primaryId && productsById[primaryId]) || coverVariant || null;

  return resolveGa4ProductImageUrl(product, {payloadCylindoUrl: payload?.cylindoUrl}) ||
    absGa4ImageUrl(basketLine?.imageGroups?.[0]?.images?.[0]?.link) ||
    '';
};

/** Basket line unit price for GA4 cart events (`priceAfterItemDiscount` preferred). */
export const resolveBasketLineUnitPrice = (line) => (
  roundGa4Currency(Number(line?.priceAfterItemDiscount ?? line?.price ?? 0)) ?? 0
);

/** Child basket line → parent line via `c_parentProductLineItemUUId`. */
export const findParentBasketLine = (line, productItems) => {
  const parentUuid = line?.c_parentProductLineItemUUId;

  if (!parentUuid) {
    return null;
  }

  return productItems.find((entry) => entry.itemId === parentUuid) ?? null;
};

/**
 * Master product from `c_selectedOptionsUI.masterProductId`.
 * @param {{ fallbackToLineProduct?: boolean }} [options] - simple lines: fallback to line `productId`
 */
export const resolveGa4MasterProduct = (line, productsById, {fallbackToLineProduct = false} = {}) => {
  const masterProductId = parseSelectedOptionsUIPayload(line?.c_selectedOptionsUI)?.masterProductId;

  if (masterProductId) {
    return productsById[masterProductId] ?? null;
  }

  if (fallbackToLineProduct && line?.productId) {
    return productsById[line.productId] ?? null;
  }

  return null;
};

/** Cart events — category source: line catalog product (variant SKU), else master. */
export const resolveGa4CategoryProduct = ({lineProductId, masterProduct, productsById}) => (
  (lineProductId ? productsById[lineProductId] : null) || masterProduct || null
);

export const resolveGa4CategoryProductForBasketLine = (line, productItems, productsById) => {
  const parentLine = findParentBasketLine(line, productItems) ?? line;
  const masterProduct = resolveGa4MasterProduct(parentLine, productsById);

  return resolveGa4CategoryProduct({
    lineProductId: line.productId,
    masterProduct,
    productsById,
  });
};

/** GA4 alias — implementation lives in `cart-utils.collectBasketCatalogProductIds`. */
export const collectGa4BasketProductIds = collectBasketCatalogProductIds;

/** Child option line from basket (view_cart / remove_from_cart). */
export const buildGa4ChildBasketLineItem = ({
  line,
  categoryProduct,
  currency,
  imageMap = {},
  nameMap = {},
  productsById = {},
  price: priceOverride,
  itemName: itemNameOverride,
}) => {
  if (!line?.productId) {
    return null;
  }

  const image = imageMap[line.productId] ||
    resolveGa4ProductImageUrl(productsById[line.productId]) ||
    '';

  return applyGa4CartItemCategories(toGa4AddToCartItemPartial({
    currency,
    itemId: line.productId,
    itemName: itemNameOverride ?? (nameMap[line.productId] || line.productName || line.productId),
    price: priceOverride ?? resolveBasketLineUnitPrice(line),
    quantity: line.quantity ?? 1,
    image,
  }), categoryProduct);
};

/** Primary cover / parent basket line (complex or simple-with-fallback master). */
export const buildGa4PrimaryCoverBasketLineItem = ({
  line,
  masterProduct = null,
  categoryProduct,
  currency,
  imageMap = {},
  productsById = {},
  price: priceOverride,
  itemName: itemNameOverride,
}) => {
  if (!line?.productId) {
    return null;
  }

  const payload = parseSelectedOptionsUIPayload(line.c_selectedOptionsUI);
  const coverVariant = masterProduct ?
    getSelectedVariantProduct(masterProduct, line.productId) :
    (productsById[line.productId] ?? null);
  const coverVariantLabel = resolveAddToCartItemVariant(coverVariant);
  const isMasterPrimary = masterProduct && (
    line.productId === masterProduct.id ||
    line.productId === masterProduct.productId
  );
  const productRecord = coverVariant || (isMasterPrimary ? masterProduct : null);
  const unitPrice = priceOverride ?? resolveBasketLineUnitPrice(line);
  const mapOpts = {currency};
  const ga4Item = applyGa4CartItemCategories(productRecord ?
    toGa4AddToCartItem(productRecord, {...mapOpts, quantity: line.quantity ?? 1}) :
    toGa4AddToCartItemPartial({
      ...mapOpts,
      itemId: line.productId,
      itemName: itemNameOverride ??
        (masterProduct?.name || line.productName || payload?.coverName || line.productId),
      itemVariant: coverVariantLabel,
      price: unitPrice,
      quantity: line.quantity ?? 1,
    }), categoryProduct);

  if (!ga4Item) {
    return null;
  }

  ga4Item.price = unitPrice;

  if (coverVariantLabel) {
    ga4Item.item_variant = coverVariantLabel;
  }

  ga4Item.image = resolveGa4PrimaryCoverImage({
    basketLine: line,
    coverVariant,
    optionImageMap: imageMap,
    payload,
    primaryItem: line,
    productsById,
  }) || ga4Item.image || '';

  return ga4Item;
};

/** Simple basket line without complex master payload. */
export const buildGa4SimpleBasketLineItem = ({
  line,
  categoryProduct,
  currency,
  productsById = {},
}) => {
  if (!line?.productId) {
    return null;
  }

  const unitPrice = resolveBasketLineUnitPrice(line);
  const product = productsById[line.productId];
  const mapOpts = {currency};
  const ga4Item = applyGa4CartItemCategories(product ?
    toGa4AddToCartItem(product, {...mapOpts, quantity: line.quantity ?? 1}) :
    toGa4AddToCartItemPartial({
      ...mapOpts,
      itemId: line.productId,
      itemName: line.productName || line.productId,
      price: unitPrice,
      quantity: line.quantity ?? 1,
    }), categoryProduct);

  if (!ga4Item) {
    return null;
  }

  ga4Item.price = unitPrice;

  ga4Item.image = (product && resolveGa4ProductImageUrl(product)) || ga4Item.image || '';

  return ga4Item;
};

/** GA4 `ecommerce.payment_type` from Stripe `payment_method_type` (PaymentElement). */
export const resolveGa4StripePaymentType = ({formatMessage, localeId, stripeMethodType}) => {
  if (stripeMethodType === 'card') {
    return formatMessage({
      defaultMessage: 'Credit Card',
      id: 'payment_selection.heading.credit_card',
    });
  }

  return formatPaymentMethod(
    {
      c_stripePaymentMethodType: stripeMethodType,
      paymentMethodId: STRIPE_PAYMENT_METHOD_ID,
    },
    formatMessage,
    localeId
  );
};

export const resolveGa4PayPalPaymentType = (formatMessage) => formatPaymentMethod(
  {paymentMethodId: 'PayPal'},
  formatMessage
);
